import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { z } from 'zod'
import { AssignmentsTab } from './-components/AssignmentsTab'
import { CategoriesTab } from './-components/CategoriesTab'
import { RecipientsTab } from './-components/RecipientsTab'
import { OverviewSection } from './-components/OverviewSection'
import { TickerUpdatesSection } from './-components/TickerUpdatesSection'
import { AlertingSection } from './-components/AlertingSection'
import { PositionBookSection } from './-components/PositionBookSection'
import { RulesSection } from './-components/RulesSection'
import { ScheduleSection } from './-components/ScheduleSection'
import { AlertRunsSection } from './-components/AlertRunsSection'
import { EngagementSection } from './-components/EngagementSection'
import { PageTitle } from '@/components/PageTitle'
import { RothRadarTargetIcon } from '@/components/shared/RothRadarTargetIcon'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { requireAnyPermission } from '@/lib/permissions/route-guards'
import {
  BACKOFFICE_PERMISSION_KEYS,
  ROTH_RADAR_PREFIX,
} from '@/lib/permissions/backoffice-permissions'
import { useCan } from '@/hooks/permissions/use-can'

const RULES_ENGINE_TABS = [
  'overview',
  'positions',
  'tickers',
  'alerting',
  'rules',
  'schedule',
] as const

const PROXY_TABS = ['assignments', 'categories', 'recipients'] as const
const ALERT_RUN_TABS = ['runs', 'recipients'] as const
// Distinct values from PROXY_TABS/ALERT_RUN_TABS's 'recipients' — tab values
// are shared across one flat z.enum below, so a bare 'overview'/'notifications'
// would collide with the Rules Engine and other sections' own tab values.
const ENGAGEMENT_TABS = [
  'engagement-overview',
  'engagement-notifications',
] as const

const SECTIONS = ['rules-engine', 'proxy', 'alert-runs', 'engagement'] as const
type Section = (typeof SECTIONS)[number]

const SECTION_TABS: Record<Section, ReadonlyArray<string>> = {
  'rules-engine': RULES_ENGINE_TABS,
  proxy: PROXY_TABS,
  'alert-runs': ALERT_RUN_TABS,
  engagement: ENGAGEMENT_TABS,
}

const SECTION_DEFAULT_TAB: Record<
  Section,
  (
    | typeof RULES_ENGINE_TABS
    | typeof PROXY_TABS
    | typeof ALERT_RUN_TABS
    | typeof ENGAGEMENT_TABS
  )[number]
> = {
  'rules-engine': 'overview',
  proxy: 'assignments',
  'alert-runs': 'runs',
  engagement: 'engagement-overview',
}

/** Which granular permission unlocks a given section (sc-1367). Each section
 * gates independently — a CPA holding only Alert Runs sees only that tab and
 * that tab's content, never an empty Rules Engine panel. */
const SECTION_PERMISSION_KEY: Record<Section, string> = {
  'rules-engine': BACKOFFICE_PERMISSION_KEYS.rothRadarRulesEngine,
  proxy: BACKOFFICE_PERMISSION_KEYS.rothRadarProxy,
  'alert-runs': BACKOFFICE_PERMISSION_KEYS.rothRadarAlertRuns,
  engagement: BACKOFFICE_PERMISSION_KEYS.rothRadarEngagement,
}

const tabSchema = z.object({
  section: z.enum(SECTIONS).optional().default('rules-engine'),
  tab: z
    .enum([
      ...RULES_ENGINE_TABS,
      ...PROXY_TABS,
      ...ALERT_RUN_TABS,
      ...ENGAGEMENT_TABS,
    ])
    .optional(),
  ruleId: z.string().optional(),
  runId: z.string().optional(),
  // Distinct from `runId`, which already names a sweep run — an Alert Run is
  // a different entity (sc-1367) and reuses that param name would collide a
  // sweep-run deep link with an alert-run deep link.
  alertRunId: z.string().optional(),
  tickerStatus: z
    .enum(['ALL', 'PENDING', 'WARMING_UP', 'ACTIVE', 'DELISTED', 'ERROR'])
    .optional(),
  // Distinct from `tickerStatus=ERROR`: `MarketSecurity.status` only becomes
  // 'ERROR' after Q3_TIERS_MAX_FAILURES (5) consecutive failures, but a
  // single sweep's `securitiesFailed` count includes any ticker that failed
  // that night. `lastError !== null` is the accurate "failed most recently"
  // signal — set on every failure, cleared on the next success.
  onlyRecentErrors: z.literal('true').optional(),
  // sc-1423. Same precedent as `tickerStatus`/`onlyRecentErrors`: read once
  // to seed the Ticker Updates tab's local view-toggle state (so a deep link
  // can land directly on the reclassify-candidates queue), then the toggle
  // is free-standing local state — not kept in sync with the URL afterward.
  tickerView: z.enum(['tracked', 'candidates']).optional(),
  // sc-1517. Position Book: `<column>_<asc|desc>` server sort, and the
  // securityKey whose clients drill-through sheet is open.
  positionSort: z.string().optional(),
  positionKey: z.string().optional(),
})

export type RothRadarRouteSearch = z.infer<typeof tabSchema>

export const Route = createFileRoute(
  '/_app/backoffice/integrations/roth-radar/',
)({
  component: RouteComponent,
  validateSearch: tabSchema,
  beforeLoad: requireAnyPermission(ROTH_RADAR_PREFIX),
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { section, tab } = Route.useSearch()
  const { has } = useCan()

  const visibleSections = SECTIONS.filter((candidate) =>
    has(SECTION_PERMISSION_KEY[candidate]),
  )

  // A CPA holding only Alert Runs must land on Alert Runs, not the hardcoded
  // 'rules-engine' default — and a stale/hand-typed `?section=` naming a
  // section this caller can't see falls back the same way. `has()` fails
  // CLOSED while permissions resolve, so this briefly shows nothing until
  // resolved; the route guard already confirmed at least one section is
  // reachable, so `visibleSections` is non-empty once resolved.
  const activeSection = visibleSections.includes(section)
    ? section
    : (visibleSections[0] ?? section)

  // Forgiving fallback: a stale or hand-edited URL can carry a `tab` that
  // doesn't belong to the active `section` (e.g. ?section=proxy&tab=rules).
  // Falling back to that section's default avoids rendering an empty panel.
  const activeTab =
    tab && SECTION_TABS[activeSection].includes(tab)
      ? tab
      : SECTION_DEFAULT_TAB[activeSection]

  const handleSectionChange = (value: string) => {
    const nextSection = value as Section
    void navigate({
      search: (prev) => ({
        ...prev,
        section: nextSection,
        tab: SECTION_DEFAULT_TAB[nextSection],
      }),
      replace: true,
    })
  }

  const handleTabChange = (value: string) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        tab: value as RothRadarRouteSearch['tab'],
      }),
      replace: true,
    })
  }

  return (
    <div className="py-4">
      <PageTitle title="RothRadar™ Integration" />

      <div className="mb-4">
        <Link
          to="/backoffice/integrations"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" />
          Integrations
        </Link>
      </div>

      <div className="mb-6 flex items-start gap-3">
        {/* Red here, muted on the advisor surfaces — see RothRadarTargetIcon.
         * The mark inherits `currentColor` from this tile, so the tint and the
         * text colour move together. */}
        <div className="flex size-9 items-center justify-center rounded-xl bg-red-500/10 text-red-700 dark:text-red-400">
          <RothRadarTargetIcon className="size-5" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            RothRadar™
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Rules engine admin workspace (tracked ticker universe, sweep runs
            and schedule, cross-client flagged positions, and rule definitions)
            plus the firm-wide proxy map from a holding RothRadar™ can&apos;t
            price directly to a generic proxy ETF whose price series stands in
            for it, plus daily CPA approval of the automated client alert send —
            a 9:30am ET cron mails exactly the names and dollars approved for
            that service date, nothing more.
          </p>
        </div>
      </div>

      <Tabs
        value={activeSection}
        onValueChange={handleSectionChange}
        className="w-full"
      >
        {/* sc-1409: the shared TabsList (components/animate-ui/components/radix/tabs.tsx)
         * is `inline-flex w-fit` and neither shrinks nor scrolls — sized-to-content
         * by design for every other page that uses it (forecasts, data gathering,
         * IRS tables, learning, saved views), so it isn't safe to change there.
         * Scoping the scroll to this route's wrapper instead keeps the strip
         * (including its absolute-positioned active-tab highlight) scrolling as
         * one unit while the page body stays put. */}
        <div className="max-w-full overflow-x-auto">
          <TabsList>
            {has(SECTION_PERMISSION_KEY['rules-engine']) && (
              <TabsTrigger value="rules-engine">Rules Engine</TabsTrigger>
            )}
            {has(SECTION_PERMISSION_KEY.proxy) && (
              // "Proxy Table" rather than bare "Proxy": this page lives under
              // Integrations beside Plaid, Stripe and Front, where "Proxy" alone
              // reads as network infrastructure. It is also what the source
              // documents call it — the Master Proxy Table, maintained by the
              // Proxy Table Manager.
              <TabsTrigger value="proxy">Proxy Table</TabsTrigger>
            )}
            {has(SECTION_PERMISSION_KEY['alert-runs']) && (
              <TabsTrigger value="alert-runs">Alert Runs</TabsTrigger>
            )}
            {has(SECTION_PERMISSION_KEY.engagement) && (
              <TabsTrigger value="engagement">Engagement</TabsTrigger>
            )}
          </TabsList>
        </div>

        {has(SECTION_PERMISSION_KEY['rules-engine']) && (
          <TabsContent value="rules-engine" className="mt-4">
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="w-full"
            >
              {/* sc-1409: see the scroll-wrapper comment on the section TabsList
               * above — same reasoning, this strip is the one that actually
               * overflows at 375px (five triggers, ~398px wide). */}
              <div className="max-w-full overflow-x-auto">
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="positions">Positions</TabsTrigger>
                  <TabsTrigger value="tickers">Ticker Updates</TabsTrigger>
                  <TabsTrigger value="alerting">Alerting</TabsTrigger>
                  <TabsTrigger value="rules">Rules</TabsTrigger>
                  <TabsTrigger value="schedule">Schedule</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="mt-4">
                <OverviewSection />
              </TabsContent>
              <TabsContent value="positions" className="mt-4">
                <PositionBookSection />
              </TabsContent>
              <TabsContent value="tickers" className="mt-4">
                <TickerUpdatesSection />
              </TabsContent>
              <TabsContent value="alerting" className="mt-4">
                <AlertingSection />
              </TabsContent>
              <TabsContent value="rules" className="mt-4">
                <RulesSection />
              </TabsContent>
              <TabsContent value="schedule" className="mt-4">
                <ScheduleSection />
              </TabsContent>
            </Tabs>
          </TabsContent>
        )}

        {has(SECTION_PERMISSION_KEY.proxy) && (
          <TabsContent value="proxy" className="mt-4">
            <Tabs
              value={activeTab}
              onValueChange={handleTabChange}
              className="w-full"
            >
              {/* sc-1409: see the scroll-wrapper comment on the section TabsList
               * above. Doesn't overflow today, but this section is
               * permission-gated so it shouldn't be left to break later. */}
              <div className="max-w-full overflow-x-auto">
                <TabsList>
                  <TabsTrigger value="assignments">Assignments</TabsTrigger>
                  <TabsTrigger value="categories">Categories</TabsTrigger>
                  <TabsTrigger value="recipients">Recipients</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="assignments" className="mt-4">
                <AssignmentsTab />
              </TabsContent>
              <TabsContent value="categories" className="mt-4">
                <CategoriesTab />
              </TabsContent>
              <TabsContent value="recipients" className="mt-4">
                <RecipientsTab />
              </TabsContent>
            </Tabs>
          </TabsContent>
        )}

        {has(SECTION_PERMISSION_KEY['alert-runs']) && (
          <TabsContent value="alert-runs" className="mt-4">
            <AlertRunsSection
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </TabsContent>
        )}

        {has(SECTION_PERMISSION_KEY.engagement) && (
          <TabsContent value="engagement" className="mt-4">
            <EngagementSection
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
